import { action, observable, computed, makeObservable } from 'mobx';
import { apiService } from '../../services/api.service';
import { utilsService } from '../../services/utils.service';

export default class NewAppUploadStore {
	constructor(master) {
		makeObservable(this);
		this.masterStateProps = master.masterStateProps;
		this.newAppMasterStore = master;
	}

	SCREEN_LIMIT = 5;
	CHARACTER_LIMIT = 80;

	/////////////////////////
	// Developer Checklist Stuff
	/////////////////////////

	// using boxed computed because the decorator causes a strange race condition
	previouslyUploadedDeveloperChecklist = computed(() => {
		let name = this.newAppMasterStore.newAppData.caFormFileName;
		let ext = this.newAppMasterStore.newAppData.caFormFileExtension
		if (name && ext) {
			return `${name}.${ext}`;
		}
	})

	@action handleDeveloperChecklistUpload(event) {
		let files = this.getFilesFromUploadEvent(event);
		this.checkFileForErrorsAndUpload({
			uploadedFiles: files,
			fileInCentralStore: this.newAppMasterStore.newAppData.caFormFileName,
			fileMetaObject: this.developerChecklist,
			fileTypeCheck: /.*\.(zip|xlsx)$/i,
			endpoint: "uploadDeveloperChecklistFile"
		})
	}

	@action removeDeveloperChecklist() {
		const success = () => {
			this.developerChecklist.isUploading = false;
			this.developerChecklist.isDeleting = false;
			this.developerChecklist.progress = 0;
			this.newAppMasterStore.clearFile("uploadDeveloperChecklistFile")
			utilsService.announceSuccess('Deletion');
			return;
		}
		const fail = (err) => {
			utilsService.announceFailure('Deletion');
			throw err;
		}
		this.developerChecklist.isDeleting = true;
		apiService.deleteDeveloperChecklist(this.newAppMasterStore.newAppData.saveId).then(success, fail);
	}

	@computed get certificationsDropzoneError() {
		if (this.developerChecklist.invalidUploadAttempted === 2) {
			return this.newAppMasterStore.FILE_SIZE_100MB_ERROR;
		} else if (this.developerChecklist.invalidUploadAttempted === 1) {
			return this.newAppMasterStore.ZIP_OR_XLSX_ERROR;
		} else if (this.newAppMasterStore.masterStateProps.errorsAreDisplayed && !this.newAppMasterStore.newAppData.caFormFileName) {
			return this.newAppMasterStore.REQUIRED_ERROR;
		}
	}


	/////////////////////////
	// App Package Stuff
	/////////////////////////

	previouslyUploadedAppPackage = computed(() => {
		return this.newAppMasterStore.newAppData.appFileName
	})

	@action handleAppPackageUpload(event) {
		let files = this.getFilesFromUploadEvent(event)	
		this.checkFileForErrorsAndUpload({
			uploadedFiles: files,
			fileInCentralStore: this.newAppMasterStore.newAppData.appFileName,
			fileMetaObject: this.appPackage,
			fileTypeCheck: /android/i.test(this.newAppMasterStore.newAppData.platform) ? /.*\.(apk|aab)$/i : /.*\.ipa$/i,
			endpoint: "uploadPackage"
		})
	}
	
	@action removeAppPackage() {
		const success = () => {
			this.appPackage.isUploading = false;
			this.appPackage.isDeleting = false;
			this.appPackage.progress = 0;
			this.newAppMasterStore.clearFile("uploadPackage")
			utilsService.announceSuccess('Deletion');
			return;
		}
		const fail = (err) => {
			utilsService.announceFailure('Deletion');
			throw err;
		}
		this.appPackage.isDeleting = true;
		apiService.deleteAppPackage(this.newAppMasterStore.newAppData.saveId).then(success, fail);
	}

	@computed get invalidFileError() {
		if (this.appPackage.invalidUploadAttempted === 3) {
			return this.newAppMasterStore.FILE_SIZE_2GB_ERROR;
		} else if (this.appPackage.invalidUploadAttempted === 1 || this.newAppMasterStore.newAppDetailsStore.appPackageIsMismatched) {
			if (this.newAppMasterStore.isAndroidApp) {
				return this.newAppMasterStore.APK_REQUIRED_ERROR;
			} else if (!this.newAppMasterStore.isAndroidApp) {
				return this.newAppMasterStore.IPA_REQUIRED_ERROR;
			}
		} else if (this.newAppMasterStore.masterStateProps.errorsAreDisplayed && this.newAppMasterStore.newAppDetailsStore.appPackageIsMissing) {
			return this.newAppMasterStore.BINARY_MISSING_ERROR;
		}
	}


	/////////////////////////
	// Screenshot and Icon Stuff
	/////////////////////////

	@action handleScreenshotUpload(event, deviceType, uploadImmediately = true ) {
		let screens = this.screens[deviceType];
		let reader = undefined;
		let files = this.getFilesFromUploadEvent(event);
		for (let file of files) {
			reader = new window.FileReader();
			reader.onloadend = (event) => {
				if (screens.length !== this.SCREEN_LIMIT) {
					if (!/.*\.png$/i.test(file.name)) {
						this.invalidUploadAttempted[deviceType] = 1;
					} else {
						let image = new Image();
						image.src = event.target.result;
						image.onload = () => {
							if (deviceType === 'icon' && image.width !== image.height) {
								this.invalidUploadAttempted[deviceType] = 2;
							} else {
								this.invalidUploadAttempted[deviceType] = 0;
								screens.push({
									imageURL: event.target.result,
									description: '',
									id: '',
									isUploading: true,
									progress: 0
								});
								let screen = screens[screens.length - 1];

								if(uploadImmediately){
									this.uploadItem({
										endpoint: 'uploadScreenshot',
										file,
										fileMetaObject: screen,
										deviceType
									}).then((response) => {
										screen.isUploading = false;
										screen.imageURL = response.imageUrl;
										screen.id = response.id;
									});
								} else {
									screen.isUploading = false;
									screen.file = file;
									screen.imageIsModified = true;
								}
								this.newAppMasterStore.updateImageData(deviceType)
							}
						}
					}
				}
			}
			reader.readAsDataURL(file);
		}
	}

	@action uploadEditedScreenshots() {
		if(this.newAppMasterStore.newAppData.imageReordered) {
			let uploadPromises = [];
			for(let device in this.screens){
				let deviceScreens = this.screens[device]
				// Images need to be uploaded in serial sequence. this creates functions that return promises.
				deviceScreens.forEach( screen => {
					if(screen.file){
						uploadPromises.push(() => {
							return this.uploadItem({
								endpoint: 'uploadScreenshot',
								file: screen.file,
								fileMetaObject: screen,
								deviceType: device
							}).then((response) => {
								screen.isUploading = false;
								screen.imageURL = response.imageUrl;
								screen.id = response.id;
								this.newAppMasterStore.updateImageData(device)
								return Promise.resolve(screen);
							})
						})
					}
				})
			}
			// this turns the array of functions into sequentially resolved promises. Modified from https://stackoverflow.com/a/41115086
			const resolveSerially = funcs => {
				return funcs.reduce((promise, func) => promise.then(result => func().then(Array.prototype.concat.bind(result))), Promise.resolve([]))
			}
			return resolveSerially(uploadPromises)
		}
		return Promise.resolve();
	}

	@action removeScreenshot(index, deviceType) {
		let screenshots = this.screens[deviceType];
		screenshots.splice(index, 1);
		this.newAppMasterStore.updateImageData(deviceType)
	}

	@action changeScreenDescription(newValue, screen, deviceType) {
		screen.description = newValue.substring(0, this.CHARACTER_LIMIT);
		screen.descriptionIsModified = true;
		this.newAppMasterStore.updateImageData(deviceType)
	}

	@action reorderScreenshot(deviceType, index, direction) {
		let screens = this.screens[deviceType];

		if (index + direction >= 0 && index + direction < screens.length) {
			let tmpScreen = screens[index];
			screens[index] = screens[index + direction];
			screens[index + direction] = tmpScreen;
		}
		this.newAppMasterStore.updateImageData(deviceType)
	}

	@computed get iconImageURL() {
		if (this.screens.icon.length > 0) {
			return this.screens.icon[0].imageURL;
		} else {
			return '';
		}
	}


	/////////////////////////
	// Utility stuff
	/////////////////////////

	@action getFilesFromUploadEvent = (event) => {
		let files = [];
		if (event.target.type === 'file') {
			files = Array.from(event.target.files);
		} else if (event.dataTransfer.files) {
			files = Array.from(event.dataTransfer.files);
		} else if (event.dataTransfer.items) {
			files = Array.from(event.dataTransfer.items).map(item => item.getAsFile());
		}
		return files;
	}

	@action fileIsValidForUpload({ file, fileMetaObject, fileTypeCheck }){
		//console.log('Inside validations');
		//console.log(file.size, file.size / 1024 / 1024 + 'MB');
		let size = file.size; // 2097163 * 1024;
		if (!fileTypeCheck.test(file.name)) {
			fileMetaObject.invalidUploadAttempted = 1;
			return false;
		} else if (size > 104857600 && /.*\.(zip|xlsx)$/i.test(file.name)) {
			fileMetaObject.invalidUploadAttempted = 2;
			return false;
		} else if (size / 1024 / 1024 > 2045) { // 2037.76 MB = 1.99GB
			//console.log("Inside the file size condition :: " , file.size);
			fileMetaObject.invalidUploadAttempted = 3;
			return false;
		} else {
			return true;
		}
	}

	@action checkFileForErrorsAndUpload({ uploadedFiles, fileInCentralStore, fileMetaObject, fileTypeCheck, endpoint }) {
		for (let file of uploadedFiles) {
			if (!fileInCentralStore && this.fileIsValidForUpload({file, fileMetaObject, fileTypeCheck})) {
				const reader = new window.FileReader();
				//console.log(fileInCentralStore);
				reader.onload = () => {
					this.uploadItem({
						endpoint,
						file,
						fileMetaObject
					})
				}
				reader.readAsDataURL(file);
			}
		}
	}

	@action uploadItem({ endpoint, file, fileMetaObject, deviceType }) {
		const success = (response) => {
			utilsService.announceSuccess('Upload');
			fileMetaObject.isUploading = false;
			this.newAppMasterStore.updateFile(endpoint, file, deviceType)

			return response.data[0];
		}
		const fail = (err) => {
			utilsService.announceFailure('Upload');
			fileMetaObject.isUploading = false;
			this.newAppMasterStore.clearFile(endpoint)
			throw err;
		}

		fileMetaObject.invalidUploadAttempted = 0;
		fileMetaObject.isUploading = true;
		fileMetaObject.progress = 0;
		if (endpoint === "uploadDeveloperChecklistFile") {
			file = utilsService.extractPayloadProperties(file);
		}

		/* Note: this operation is allowed for both released an unreleased apps.
		applicationIdentifier can be either saveId or appPsk. */
		const appData = this.newAppMasterStore.newAppData;
		const applicationIdentifier = appData.saveId || `${appData.applicationId}`;
		return apiService[endpoint](file, applicationIdentifier, fileMetaObject, deviceType)
			.then(success, fail);
	}

	@action changeDeviceType(newValue) {
		this.newAppMasterStore.newAppData.device = newValue;
	}

	@action resetFields() {
		this.developerChecklist = {
			filename: this.previouslyUploadedDeveloperChecklist.get(),
			isUploading: false,
			isDeleting: false,
			progress: 0,
			invalidUploadAttempted: null
		};

		this.appPackage = {
			filename: this.previouslyUploadedAppPackage.get(),
			isUploading: false,
			isDeleting: false,
			progress: 0,
			invalidUploadAttempted: null
		};

		this.screens = {
			icon: [],
			mobile: [],
			tablet: []
		}
		this.newAppMasterStore.updateImageData()
	}

	@observable invalidUploadAttempted = {
		icon: false,
		mobile: false,
		tablet: false
	}

	@observable developerChecklist = {
		filename: null,
		isUploading: false,
		isDeleting: false,
		progress: 0,
		invalidUploadAttempted: null
	};

	@observable appPackage = {
		filename: null,
		isUploading: false,
		isDeleting: false,
		progress: 0,
		invalidUploadAttempted: null
	};

	@observable screens = {
		icon: [],
		mobile: [],
		tablet: []
	}
}